import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  MarkdownText,
  Pane,
  Pill,
  RestrictedButton,
  Text,
} from '@fullstory/components';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ThumbToggle } from '../ThumbToggle';
import { AISummaryFeedbackModal } from './AISummaryFeedbackModal/AISummaryFeedbackModal';
import css from './AISummaryPane.module.scss';
import { LoadingShimmer } from './LoadingShimmer/LoadingShimmer';
import { StoryAISummaryFeedback } from '@fullstory/analytics';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { intl } from '@fullstory/intl';
import { useStoryAiEnabled } from '../../hooks/useStoryAiEnabled';
import ReactMarkdown from 'react-markdown';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

export interface AISummaryPaneProps {
  /** The summary content */
  content?: string;
  /** Optional tags to display with the summary */
  tags?: string[];
  /** Whether the summary is currently loading */
  isLoading?: boolean;
  /** Error message if the summary generation failed */
  error?: string;
  /** Callback when generate summary is clicked */
  onGenerateSummary?: () => void;
  /** Whether the generate summary button should be disabled */
  generateDisabled?: boolean;
  /** Whether the summary pane is in small view */
  smallView?: boolean;
  /** Whether the summary pane is expanded */
  expanded?: boolean;
  /** Callback when the summary pane is expanded */
  onExpand?: () => void;
  /** Custom title for the summary pane */
  title?: React.ReactNode;
  /** Custom loading message */
  loadingMessage?: React.ReactNode;
  /** Optional link to jump to the moment */
  jumpToMomentLink?: React.ComponentProps<typeof Link>['to'];
  /** Callback when feedback is provided */
  onFeedback?: (isHelpful: boolean, details?: Partial<StoryAISummaryFeedback>) => void;
  /** Optional disabled message */
  disabledMessage?: string;
}

export const AISummaryPane: React.FC<AISummaryPaneProps> = ({
  content,
  tags,
  isLoading = false,
  error,
  onGenerateSummary,
  generateDisabled = false,
  smallView = false,
  expanded = false,
  onExpand,
  title = <FormattedMessage defaultMessage="Last 10 sessions at a glance" id="9uZgA/" />,
  loadingMessage = (
    <FormattedMessage defaultMessage="Summarizing highlights from recent sessions" id="6lygsv" />
  ),
  jumpToMomentLink,
  onFeedback,
  disabledMessage,
}) => {
  const { hasSummaryBit, summariesOptedOut } = useStoryAiEnabled();
  const useNewSessionPlaylist = useFlags().featureFlags().AppNewSessionPlaylistUi;

  const displayContent = useMemo(() => {
    if (summariesOptedOut) {
      return null;
    }

    if (!hasSummaryBit) {
      return <NotAvailableState />;
    }

    if (!content && !isLoading && !error) {
      return (
        <ZeroState
          onGenerateSummary={onGenerateSummary}
          isLoading={isLoading}
          generateDisabled={generateDisabled}
          disabledMessage={disabledMessage}
          smallView={smallView}
        />
      );
    }
    if (isLoading) {
      return <LoadingState loadingMessage={loadingMessage} smallView={smallView} />;
    }
    if (error) {
      return (
        <Flex justifyContent="flex-start" gap={8}>
          <Text appearance="bodySmall" color="destructive">
            {error}
          </Text>
          <Button
            variant="outline"
            size="x-small"
            onClick={onGenerateSummary}
            disabled={isLoading || generateDisabled}
          >
            <FormattedMessage defaultMessage="Try again" id="FazwRl" />
          </Button>
        </Flex>
      );
    }
    if (content) {
      return (
        <ContentState
          content={content}
          tags={tags}
          smallView={smallView}
          expanded={expanded}
          onExpand={onExpand}
          jumpToMomentLink={jumpToMomentLink}
          onFeedback={onFeedback}
        />
      );
    }
    return <></>;
  }, [
    summariesOptedOut,
    hasSummaryBit,
    content,
    isLoading,
    error,
    onGenerateSummary,
    generateDisabled,
    disabledMessage,
    loadingMessage,
    smallView,
    tags,
    expanded,
    onExpand,
    jumpToMomentLink,
    onFeedback,
  ]);

  return (
    <Pane border="standard" padding={smallView && !useNewSessionPlaylist ? 8 : 16} borderRadius={4}>
      <Flex column alignItems="flex-start" gap={8}>
        {!useNewSessionPlaylist && <Text appearance="bodyMediumBold">{title}</Text>}
        <Box width={1}>{displayContent}</Box>
      </Flex>
    </Pane>
  );
};

interface ZeroStateProps {
  onGenerateSummary?: () => void;
  isLoading: boolean;
  generateDisabled: boolean;
  disabledMessage?: string;
  smallView: boolean;
}

const ZeroState: React.FC<ZeroStateProps> = ({
  onGenerateSummary,
  isLoading,
  generateDisabled,
  disabledMessage = intl.formatMessage({
    defaultMessage: 'No matching sessions to summarize',
    id: 'pbsJcJ',
  }),
  smallView,
}) => {
  const useNewSessionPlaylist = useFlags().featureFlags().AppNewSessionPlaylistUi;
  let message = disabledMessage;
  if (!generateDisabled) {
    if (!useNewSessionPlaylist) {
      message = intl.formatMessage({
        defaultMessage: 'Summarize highlights from recent sessions',
        id: 'WFmFzs',
      });
    } else if (smallView) {
      message = intl.formatMessage({
        defaultMessage: 'Summarize session highlights with StoryAI',
        id: 'mEw9bl',
      });
    } else {
      message = intl.formatMessage({
        defaultMessage: 'Summarize highlights from last 10 sessions',
        id: 'VHbR5O',
      });
    }
  }
  return (
    <Box width={1}>
      <Flex column alignItems="flex-start" gap={8}>
        <Flex gap={8} flexWrap="wrap" justifyContent="flex-start">
          <Text
            appearance="bodyMedium"
            color={useNewSessionPlaylist ? 'default' : 'secondary'}
            noWrap
          >
            {message}
          </Text>
          <Box>
            <Button
              variant={useNewSessionPlaylist ? 'fill-secondary' : 'outline'}
              size="x-small"
              onClick={onGenerateSummary}
              disabled={isLoading || generateDisabled}
            >
              <FormattedMessage defaultMessage="Generate summary" id="WlsIqw" />
            </Button>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

const NotAvailableState = () => (
  <Box width={1}>
    <Flex column alignItems="flex-start" gap={8}>
      <Flex gap={4} justifyContent="flex-start">
        <Text appearance="bodyMedium" color="secondary" noWrap>
          <FormattedMessage
            defaultMessage="Summarize highlights from recent sessions"
            id="WFmFzs"
          />
        </Text>
        <Box>
          <RestrictedButton
            size="x-small"
            variant="fill-secondary"
            tooltip="Upgrade your account to get this feature."
            disabled
            tooltipAction={{
              text: intl.formatMessage({ defaultMessage: 'Learn more', id: 'TdTXXf' }),
              href: 'https://help.fullstory.com/hc/en-us/articles/30944300173719-Overview-of-StoryAI',
            }}
          >
            <FormattedMessage defaultMessage="Generate summary" id="WlsIqw" />
          </RestrictedButton>
        </Box>
      </Flex>
    </Flex>
  </Box>
);

const LoadingState: React.FC<{ smallView?: boolean; loadingMessage: React.ReactNode }> = ({
  smallView = false,
  loadingMessage,
}) => (
  <Box width={1}>
    <Flex column gap={8} alignItems="flex-start">
      <Flex gap={4} alignItems="center">
        <Text appearance="bodyMedium" color="ai">
          {loadingMessage}
        </Text>
        <Icon name="ai-animated" color="ai" />
      </Flex>
      <LoadingShimmer lines={smallView ? 1 : 3} />
    </Flex>
  </Box>
);

interface ContentStateProps {
  content: string;
  tags?: string[];
  smallView?: boolean;
  expanded?: boolean;
  onExpand?: () => void;
  jumpToMomentLink?: React.ComponentProps<typeof Link>['to'];
  onFeedback?: (isHelpful: boolean, details?: Partial<StoryAISummaryFeedback>) => void;
}

const ContentState: React.FC<ContentStateProps> = ({
  content,
  tags,
  smallView,
  expanded: expandedProp,
  onExpand,
  jumpToMomentLink,
  onFeedback,
}) => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackState, setFeedbackState] = useState('none');

  const expandable = !!onExpand;
  const expanded = expandedProp || !smallView || !expandable;
  const flags = useFlags();
  const tagsEnabled = flags.featureFlags().AppSummaryTags;
  const notifications = useNotifications();

  const handlePositiveFeedback = () => {
    if (onFeedback && feedbackState !== 'positive') {
      onFeedback(true);
      setFeedbackState('positive');
    }
  };

  const handleNegativeFeedback = () => {
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = (isHelpful: boolean, details: Partial<StoryAISummaryFeedback>) => {
    if (onFeedback && feedbackState !== 'negative') {
      onFeedback(isHelpful, details);
      setFeedbackState('negative');
    }
  };

  const copyToClipboard = useCallback(() => {
    const div = document.createElement('div');
    const root = createRoot(div);
    flushSync(() => {
      root.render(<ReactMarkdown>{content}</ReactMarkdown>);
    });

    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([div.innerHTML], {
            type: 'text/html',
          }),
          'text/plain': new Blob([content], {
            type: 'text/plain',
          }),
        }),
      ])
      .then(() => {
        notifications.success(
          intl.formatMessage({ defaultMessage: 'Copied to clipboard', id: '93oVTh' }),
        );
      })
      .catch(() => {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to copy to clipboard',
            id: 'ce5Hp1',
          }),
        );
      });
  }, [content, notifications]);

  return (
    <Box width={1} className={css.content}>
      <Flex column alignItems="flex-start" justifyContent="flex-start" gap={8}>
        {expanded ? (
          <MarkdownText text={content} />
        ) : (
          <Box maxHeight={80} overflow="hidden" className={css.truncatedContent}>
            <MarkdownText text={content} />
          </Box>
        )}
        {tagsEnabled && (
          <>
            {tags === undefined || tags.length === 0 ? (
              <LoadingShimmer lines={1} className={css.tagShimmer} />
            ) : (
              expanded && (
                <Flex gap={8} flexWrap="wrap" justifyContent="flex-start">
                  {tags.map(tag => (
                    <Pill variant="outline" key={tag}>
                      {tag}
                    </Pill>
                  ))}
                </Flex>
              )
            )}
          </>
        )}
        {expandable && (
          <Button variant="minimal" size="x-small" onClick={onExpand} testId="expand-summary">
            <span style={{ textDecoration: 'underline' }}>
              {expanded ? (
                <FormattedMessage defaultMessage="Show less" id="qyJtWy" />
              ) : (
                <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
              )}
            </span>
          </Button>
        )}
        {jumpToMomentLink && (
          <Link className={css.link} to={jumpToMomentLink}>
            <Text appearance="bodySmall" color="secondary">
              <FormattedMessage defaultMessage="Jump to this moment" id="Vxc0Q+" />
            </Text>
          </Link>
        )}
        <Text appearance="bodyXSmall" color="secondary">
          <FormattedMessage
            defaultMessage="Content generated by StoryAI may be inaccurate."
            id="FAccpJ"
          />
        </Text>
        {onFeedback && (
          <Box display="flex" gap={8} alignItems="center" width="100%">
            <Text appearance="bodySmall">
              <FormattedMessage defaultMessage="Was the summary helpful?" id="zVlAK0" />
            </Text>
            <ThumbToggle
              disabled={feedbackState !== 'none'}
              direction="up"
              toggled={feedbackState === 'positive'}
              title={intl.formatMessage({ defaultMessage: 'Helpful', id: '5vwKkg' })}
              onToggle={handlePositiveFeedback}
            />
            <ThumbToggle
              disabled={feedbackState !== 'none'}
              direction="down"
              toggled={feedbackState === 'negative'}
              title={intl.formatMessage({ defaultMessage: 'Not Helpful', id: 'PTqEp4' })}
              onToggle={handleNegativeFeedback}
            />
            <Box style={{ marginLeft: 'auto' }}>
              <IconButton
                iconName="copy"
                title={intl.formatMessage({ defaultMessage: 'Copy', id: '4l6vz1' })}
                onClick={() => copyToClipboard()}
              />
            </Box>
          </Box>
        )}
      </Flex>

      {onFeedback && (
        <AISummaryFeedbackModal
          open={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onFeedback={handleFeedbackSubmit}
        />
      )}
    </Box>
  );
};
